import React from 'react';
import Icon from '../../../components/AppIcon';

const ContactHero = () => {
  const supportFeatures = [
    {
      icon: 'Clock',
      title: '24-Hour Response',
      description: 'We respond to all inquiries within 24 hours during business days'
    },
    {
      icon: 'Users',
      title: 'Family-Focused Support',
      description: 'Our team understands the unique challenges of family organization'
    },
    {
      icon: 'Shield',
      title: 'Privacy-First Communication',
      description: 'All communications are secure and your privacy is protected'
    },
    {
      icon: 'Heart',
      title: 'Personalized Help',
      description: 'Every family is unique, and so is our approach to helping you'
    }
  ];

  return (
    <div className="relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-10 left-10 w-20 h-20 border-2 border-primary transform rotate-45 rounded-lg"></div>
        <div className="absolute top-32 right-20 w-16 h-16 border-2 border-secondary transform rotate-12 rounded-lg"></div>
        <div className="absolute bottom-20 left-1/4 w-12 h-12 border-2 border-accent transform -rotate-12 rounded-lg"></div>
        <div className="absolute bottom-32 right-1/3 w-14 h-14 border-2 border-gold transform rotate-45 rounded-lg"></div>
      </div>
      <div className="relative">
        {/* Main Hero Content */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-2xl mb-6 breathing">
            <Icon name="MessageCircle" size={32} className="text-white" />
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground mb-6">
            We're Here to{' '}
            <span className="bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
              Help
            </span>
          </h1>
          
          <p className="text-xl text-text-secondary max-w-3xl mx-auto mb-8 leading-relaxed">
            Whether you have questions about Cynizee, need partnership information, or want to share feedback, 
            our family-focused team is ready to assist you on your journey from chaos to calm.
          </p>

          <div className="flex flex-wrap justify-center gap-4 text-sm text-text-secondary">
            <div className="flex items-center space-x-2">
              <Icon name="Mail" size={16} className="text-primary" />
              <span>support@cynizee.com</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Clock" size={16} className="text-secondary" />
              <span>Response within 24 hours</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="MapPin" size={16} className="text-accent" />
              <span>Based in the United States</span>
            </div>
          </div>
        </div>

        {/* Support Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {supportFeatures?.map((feature, index) => (
            <div
              key={index}
              className="bg-card rounded-xl p-6 border border-border shadow-soft hover:shadow-elevation transition-all duration-300 hover:-translate-y-1 group"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-lg flex items-center justify-center mb-4 group-hover:from-primary/20 group-hover:to-secondary/20 transition-all duration-300">
                <Icon name={feature?.icon} size={24} className="text-primary group-hover:scale-110 transition-transform duration-300" />
              </div>
              <h3 className="font-heading font-semibold text-foreground mb-2">
                {feature?.title}
              </h3>
              <p className="text-sm text-text-secondary leading-relaxed">
                {feature?.description}
              </p>
            </div>
          ))}
        </div>

        {/* Contact Methods Preview */}
        <div className="bg-gradient-to-r from-muted/50 to-card/50 rounded-2xl p-8 border border-border">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-heading font-bold text-foreground mb-3">
              Multiple Ways to Connect
            </h2>
            <p className="text-text-secondary">
              Choose the communication method that works best for your family's needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary rounded-xl flex items-center justify-center mx-auto mb-4">
                <Icon name="Mail" size={28} className="text-white" />
              </div>
              <h3 className="font-heading font-semibold text-foreground mb-2">Email Support</h3>
              <p className="text-sm text-text-secondary">
                Detailed responses for complex questions and partnership inquiries
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-accent rounded-xl flex items-center justify-center mx-auto mb-4">
                <Icon name="Users" size={28} className="text-white" />
              </div>
              <h3 className="font-heading font-semibold text-foreground mb-2">Social Media</h3>
              <p className="text-sm text-text-secondary">
                Connect with our community and get quick updates on progress
              </p>
            </div>
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center space-x-6 text-sm text-text-secondary">
            <div className="flex items-center space-x-2">
              <Icon name="Shield" size={16} className="text-success" />
              <span>Privacy Protected</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="CheckCircle" size={16} className="text-success" />
              <span>Family-Safe Communication</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Lock" size={16} className="text-success" />
              <span>Secure & Confidential</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactHero;